import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import { StatusBadge } from '../components/StatusBadge.js';

interface Repo {
  org: string;
  repo: string;
  status: string;
  tier_floor: number | null;
  council: string | null;
  last_verdict_at: string | null;
}

export function RepoOverview() {
  const [orgFilter, setOrgFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('');

  const { data, isLoading, error } = useApiQuery<Repo[]>(['repos'], '/api/repos');

  const rows = (data ?? []).filter(r => {
    if (orgFilter && !r.org.toLowerCase().includes(orgFilter.toLowerCase())) return false;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (tierFilter !== '' && r.tier_floor !== null && r.tier_floor < Number(tierFilter)) return false;
    return true;
  });

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">Repos</h2>

      <div className="flex gap-3 mb-4 flex-wrap">
        <input
          type="text"
          placeholder="Filter by org"
          value={orgFilter}
          onChange={e => setOrgFilter(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm w-48"
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="shadow">shadow</option>
          <option value="enabled">enabled</option>
          <option value="paused">paused</option>
        </select>
        <input
          type="number"
          placeholder="Min tier floor"
          value={tierFilter}
          onChange={e => setTierFilter(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm w-32"
        />
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">Error: {String(error)}</p>}

      {!isLoading && !error && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border rounded-lg text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium text-gray-600">Org</th>
                <th className="px-4 py-2 font-medium text-gray-600">Repo</th>
                <th className="px-4 py-2 font-medium text-gray-600">Status</th>
                <th className="px-4 py-2 font-medium text-gray-600">Tier Floor</th>
                <th className="px-4 py-2 font-medium text-gray-600">Council</th>
                <th className="px-4 py-2 font-medium text-gray-600">Last Verdict</th>
                <th className="px-4 py-2 font-medium text-gray-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-400">No repos found.</td>
                </tr>
              )}
              {rows.map(r => (
                <tr key={`${r.org}/${r.repo}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{r.org}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.repo}</td>
                  <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-2">{r.tier_floor ?? '—'}</td>
                  <td className="px-4 py-2">{r.council ?? '—'}</td>
                  <td className="px-4 py-2 text-gray-500">
                    {r.last_verdict_at ? new Date(r.last_verdict_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-2">
                    <Link to={`/repos/${r.org}/${r.repo}`} className="text-blue-600 hover:underline text-xs">
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
